import { useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import BaseHeadline from '../../../components/general/BaseHeadline'
import { BaseCardContainer } from '../../../components/general/BaseCard'
import BaseIcon from '../../../components/general/BaseIcon'
import { TStandardChaos } from '../../../types/index'

gsap.registerPlugin(ScrollTrigger)

type StandardSoftwareChaosProps = Pick<TStandardChaos, 'heading' | 'problem_card'>

type ChaosTarget = {
    xPercent: number
    yPercent: number
    rotate: number
}

type MobileExitTarget = {
    xPercent: number
    yPercent: number
    rotate: number
}

function getCardTargets(total: number, isMobile: boolean): ChaosTarget[] {
    const desktopTargets: ChaosTarget[] = [
        { xPercent: -145, yPercent: -110, rotate: -6 },
        { xPercent: 145, yPercent: -100, rotate: 5 },
        { xPercent: -135, yPercent: 112, rotate: 4 },
        { xPercent: 135, yPercent: 105, rotate: -5 },
    ]

    const mobileTargets: ChaosTarget[] = [
        { xPercent: -92, yPercent: -84, rotate: -6 },
        { xPercent: 92, yPercent: -76, rotate: 5 },
        { xPercent: -88, yPercent: 82, rotate: 4 },
        { xPercent: 88, yPercent: 78, rotate: -5 },
    ]

    if (total <= 4) {
        return (isMobile ? mobileTargets : desktopTargets).slice(0, total)
    }

    const radiusX = isMobile ? 92 : 145
    const radiusY = isMobile ? 84 : 110

    return Array.from({ length: total }, (_, idx) => {
        const angle = (Math.PI * 2 * idx) / total - Math.PI / 2

        return {
            xPercent: Math.cos(angle) * radiusX,
            yPercent: Math.sin(angle) * radiusY,
            rotate: Math.sin(angle * 1.8) * 8,
        }
    })
}

function getMobileExitTargets(total: number): MobileExitTarget[] {
    const baseTargets: MobileExitTarget[] = [
        { xPercent: -118, yPercent: -42, rotate: -15 },
        { xPercent: 118, yPercent: -34, rotate: 14 },
        { xPercent: -112, yPercent: 52, rotate: -12 },
        { xPercent: 112, yPercent: 58, rotate: 11 },
    ]

    if (total <= 4) return baseTargets.slice(0, total)

    const radiusX = 118
    const radiusY = 56

    return Array.from({ length: total }, (_, idx) => {
        const angle = (Math.PI * 2 * idx) / total - Math.PI / 2

        return {
            xPercent: Math.cos(angle) * radiusX,
            yPercent: Math.sin(angle) * radiusY,
            rotate: Math.sin(angle * 1.7) * 14,
        }
    })
}

export default function StandardSoftwareChaos({ heading, problem_card }: StandardSoftwareChaosProps) {
    const sectionRef = useRef<HTMLElement>(null)
    const textRef = useRef<HTMLDivElement>(null)
    const cardRefs = useRef<(HTMLDivElement | null)[]>([])

    const safeCards = problem_card ?? []

    useGSAP(() => {
        if (!cardRefs.current.length) return

        const initialRotations = [-14, 8, -8, 15]

        cardRefs.current.forEach((card, idx) => {
            if (!card) return

            gsap.set(card, {
                xPercent: 0,
                yPercent: 0,
                rotate: initialRotations[idx % initialRotations.length],
                zIndex: safeCards.length - idx,
                autoAlpha: 1,
            })
        })

        gsap.set(textRef.current, {
            autoAlpha: 0,
            scale: 0.88,
            y: 24,
        })

        const mm = gsap.matchMedia()

        mm.add('(min-width: 1024px)', () => {
            const desktopTargets = getCardTargets(safeCards.length, false)
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 1.25,
                },
            })

            tl.to({}, { duration: 14 })

            cardRefs.current.forEach((card, idx) => {
                if (!card) return

                const target = desktopTargets[idx]
                if (!target) return

                tl.to(
                    card,
                    {
                        xPercent: target.xPercent,
                        yPercent: target.yPercent,
                        rotate: target.rotate,
                        duration: 38,
                        ease: 'power2.inOut',
                    },
                    14,
                )
            })

            tl.to(
                textRef.current,
                {
                    autoAlpha: 1,
                    scale: 1,
                    y: 0,
                    duration: 20,
                    ease: 'power2.out',
                },
                22,
            )

            tl.to({}, { duration: 18 }, 56)
        })

        mm.add('(max-width: 1023px)', () => {
            const mobileExitTargets = getMobileExitTargets(safeCards.length)
            const cardPhaseLength = Math.max(14, safeCards.length * 16)
            const slot = cardPhaseLength / Math.max(safeCards.length, 1)
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 1.05,
                },
            })

            tl.to({}, { duration: 10 })

            cardRefs.current.forEach((card, idx) => {
                if (!card) return

                const startAt = 10 + idx * slot
                const exitTarget = mobileExitTargets[idx]
                if (!exitTarget) return

                tl.to(
                    card,
                    {
                        rotate: 0,
                        scale: 1.02,
                        duration: slot * 0.22,
                        ease: 'power1.out',
                    },
                    startAt,
                )

                tl.to(
                    card,
                    {
                        xPercent: exitTarget.xPercent,
                        yPercent: exitTarget.yPercent,
                        rotate: exitTarget.rotate,
                        autoAlpha: 0.28,
                        scale: 0.96,
                        duration: slot * 0.58,
                        ease: 'power2.inOut',
                    },
                    startAt + slot * 0.3,
                )
            })

            tl.to(
                textRef.current,
                {
                    autoAlpha: 1,
                    scale: 1,
                    y: 0,
                    duration: 18,
                    ease: 'power2.out',
                },
                10 + cardPhaseLength + 2,
            )

            tl.to({}, { duration: 14 }, 10 + cardPhaseLength + 18)
        })

        return () => {
            mm.revert()
        }
    }, { scope: sectionRef, dependencies: [safeCards.length] })

    return (
        <section ref={sectionRef} className="relative h-[420vh] lg:h-[320vh]">
            <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden px-4 md:px-8">
                <div
                    ref={textRef}
                    className="absolute z-0 text-center max-w-3xl px-4 pointer-events-none"
                    style={{ opacity: 0 }}
                >
                    <BaseHeadline
                        label={heading?.label}
                        labelColor="secondary"
                        headline={heading?.headline}
                        text={heading?.text}
                        tag="h2"
                        center
                    />
                </div>

                <div className="relative w-full max-w-[280px] md:max-w-sm flex items-center justify-center -z-10 lg:z-10 pointer-events-none">
                    {safeCards.map((card, idx) => (
                        <div
                            key={`${card.id}-${idx}`}
                            ref={(el) => { cardRefs.current[idx] = el }}
                            className="absolute w-64 md:w-80"
                        >
                            <BaseCardContainer className="!bg-[#1A1919] !border-[rgba(73,72,71,0.3)] rounded-2xl p-6 md:p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] pointer-events-auto gap-0">
                                <div className="w-12 h-12 rounded-xl bg-[rgba(241,139,51,0.05)] flex items-center justify-center mb-6 border border-[rgba(241,139,51,0.2)]">
                                    <BaseIcon name={card.lucideIcon} color="#F18B33" size={24} />
                                </div>
                                <h3 className="font-headline font-bold text-lg md:text-xl text-white mb-3">{card.title}</h3>
                                <p className="">{card.text}</p>
                            </BaseCardContainer>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
